/* tslint:disable:component-selector import-spacing no-trailing-whitespace no-inferrable-types */
import {
  AfterViewInit,
  Component,
  OnDestroy, OnInit, ViewChild,
} from '@angular/core';

import {
  forkJoin,
  Observable, of,
  Subscriber
} from "rxjs";

import { catchError, first, tap } from 'rxjs/operators';

import {Logger}                    from '@/util/logger';

import {
  empty,
  errorAsText,
  basename, isset, ago,
} from '@/util/util';
import {IdeService}                from '@/services/ide.service';
import {ModalService}              from '@/services/modal.service';
import {JobWatcherService}         from '@/services/job.watcher.service';
import {BlobService}               from '@/services/blob.service';
import {
  SimulationJob,
  SimulationStatusType
}                                  from '@/models/sim/simulation.job';
import {StringLineViewerMetadata}  from '@/screens/ide/string.line.viewer.metadata';
import {LineSimService}            from '@/services/line.sim.service';
import {SimConfig}                 from '@/models/sim/sim.config';
import {SimpleSimJob}              from '@/models/sim/simple.sim.job';
import {RemoteSimJob}              from '@/models/sim/remote.sim.job';
import {TextEditorMetadata}        from "@/screens/ide/text.editor.metadata";
import {JSONBrowserMetadata}       from "@/screens/ide/json.browser.metadata";
import {Scenario}                  from "@/models/scenario/scenario";
import {ConnectionViewerMetadata}  from "@/screens/ide/connection.viewer.metadata";
import {ScenarioBrowserMetadata}   from "@/screens/ide/scenario.browser.metadata";
import {DiversionEditorMetadata}   from "@/screens/ide/diversion.editor.metadata";
import {TSOEditorMetadata}         from "@/screens/ide/t.s.o.editor.metadata";
import {WorkBloxEditorMetadata}    from "@/screens/ide/work.blox.editor.metadata";
import {ContentAwareTyping}        from "@/helpers/content.aware.typing";
import {TaskUpdate}                from "@/models/task.update";
import {TaskArtifactFetch}         from "@/models/task.artifact.fetch";
import {TaskArtifactFetchComplete} from "@/models/task.artifact.fetch.complete";
import {TaskProgressService}       from "@/services/task.progress.service";
import {TaskArtifactFetchFailed}   from "@/models/task.artifact.fetch.failed";

import * as $                      from 'jquery';
import 'jquery-ui-dist/jquery-ui';
import 'gasparesganga-jquery-loading-overlay'; /* https://gasparesganga.com/labs/jquery-loading-overlay/ */
import 'jquery-contextmenu';

import { TaskRemoteSimulationDelete } from '@/models/task.remote.simulation.delete';
import { TaskRemoteSimulationDeleteComplete } from '@/models/task.remote.simulation.delete.complete';
import { TaskRemoteSimulationDeleteFailed } from '@/models/task.remote.simulation.delete.failed';

import { DataView } from 'primeng/dataview';

@Component({
  selector: 'jobs-viewer-component',
  template: `

    <!-- the job list across all simulation engines -->

    <div class="jobs-viewer-outer-wrapper">

      <!-- job filtering, actions etc. -->

      <div class="jobs-viewer-actions">

        <p-toolbar>

          <div class="p-toolbar-group-start">

            <!-- manual refresh of job list -->

            <p-button
              (click)="fetchJobs()"
              class="jobs-viewer-actions-icon-button"
              icon="pi pi-refresh"
              title="Manually Refresh List"
              [disabled]="busy ? 'true': 'false' "
            />

            <p-button
              (click)="selectAllOnPage()"
              class="jobs-viewer-actions-icon-button"
              icon="fa-solid fa-list-check"
              title="Select All On Page"
              [disabled]="busy ? 'true': 'false' "
            />

            <p-button
              (click)="clearSelection()"
              class="jobs-viewer-actions-icon-button"
              icon="fa-solid fa-ban"
              title="Clear Selection"
              [disabled]="(busy || (selection.length == 0))  ? 'true': 'false' "
            />

            <p-button
              (click)="deleteSelected()"
              class="jobs-viewer-actions-icon-button action-delete"
              icon="fa-solid fa-trash-can"
              title="Delete Selected Jobs"
              [disabled]="(busy || (selection.length == 0)) ? 'true': 'false' "
            />

          </div>

          <div class="p-toolbar-group-center">

            <!-- sort by date (default) -->

            <p-button
              label="Date"
              [icon]="iconForDateSort()"
              (onClick)="toggleDateSorting()"
              class="jobs-viewer-sort-by-date-button"
              [disabled]="busy ? 'true': 'false' "
              title="Order the list by Date"
            />

            <!-- sort by name -->

            <p-button
              label="Name"
              [icon]="iconForNameSort()"
              (onClick)="toggleNameSorting()"
              class="jobs-viewer-sort-by-name-button"
              [disabled]="busy ? 'true': 'false' "
              title="Order the list by Name"
            />

            <!-- filter by name -->

            <input
              class="job-viewer-search-input p-inputtext"
              [(ngModel)]="searchFilter"
              (change)="stringSearch()"
              (keydown.enter)="stringSearch()"
              pInputText
              placeholder="Partial Name..."
              title="Search for Scenarios"
              [disabled]="busy ? 'true': 'false' "
              tabIndex="1"
            />

            <p-button
              (click)="stringSearch()"
              class="jobs-viewer-actions-icon-button"
              icon="pi pi-filter"
              title="Presss RETURN or Click to Search"
              [disabled]="busy ? 'true': 'false' "
            />

            <!-- set the search mode -->

            <p-selectbutton
              [options]="searchModes"
              dataKey="value"
              [(ngModel)]="matchMode"
              optionLabel="name"
              optionValue="value"
              (onChange)="searchModeUpdated($event)"
              class="jobs-viewer-search-mode-select"
            >
              <ng-template #item let-item>
                <i title="{{item.title}}" [class]="item.icon"></i>
              </ng-template>

            </p-selectbutton>

            <!-- filter by date -->

            <p-datepicker
              [(ngModel)]="rangeDates"
              selectionMode="range"
              placeholder="Date Range..."
              [readonlyInput]="true"
              class="jobs-viewer-filter-by-datepicker"
              [showWeek]="true"
              [disabled]="busy ? 'true': 'false' "
              [numberOfMonths]="2"
              [showButtonBar]="true"
              (onClose)="dateRangeChanged()"
              tabindex="2"
            />

          </div>

          <div class="p-toolbar-group-end">

            <!-- drag n' drop scenarios to launch (will pop the launch dialog) -->

            <div class="jobs-scenario-drop-zone" title="Drag and Drop Scenarios to Simulate"
                 title="Drop Scenarios to Launch Simulations">
              <input id="jobsFileBrowser" multiple type="file"
                     style="visibility:hidden; width: 0; height: 0; margin: 0; padding: 0;"/>
              <p-button onclick="$('#jobsFileBrowser').click();" label="Browse"/>
              <span>Drop Scenarios</span>
            </div>

          </div>

        </p-toolbar>

      </div>

      <!-- the job list -->

      <ul class="jobs-viewer-job-list">

        <p-dataView
          #dv
          [emptyMessage]="'No matches.'"
          [value]="jobList"
          [first]="firstRow"
          [totalRecords]="totalRows"
          [rows]="pageSize"
          [rowsPerPageOptions]="sizeOptions"
          [paginator]="true"
          [pageLinks]="5"
          [lazy]="true"
          [loading]="busy"
          layout="list"
          [showCurrentPageReport]="true"
          [paginatorPosition]="'top'"
          [currentPageReportTemplate]="'Page {currentPage} of {totalPages}, Sims: {totalRecords}'"
          (onLazyLoad)="lazyLoad($event)"
        >

          <!-- what to display when there are no jobs -->

          <div *ngIf="jobList.length == 0; else list">
            <p>Please wait...</p>
          </div>

          <!-- we have some jobs -->

          <ng-template #list let-jobs>

            <!-- for each row / job -->

            <div
              class="jobs-viewer-grid-wrapper grid grid-nogutter">

              <div class="col-12 job-viewer-list-item"
                   [attr.data-name]="job.name"
                   [attr.data-progress]="job.progress"
                   [attr.data-status]="SimulationJob.statusString(job.status)"
                   [attr.data-jobid]="job.simKey()"
                   [class.job-viewer-list-item-selected]="isSelected(job)"
                   [class.job-viewer-list-item-cursor]="isCursorPosition(job)"
                   [class.compact-layout]="compact"
                   id="job-{{job.simKey()}}"
                   [draggable]="jobIsDraggable(job)"
                   (dragstart)="startDrag($event, job)"
                   (click)="jobClicked($event, job)"
                   (keydown)="jobKeyPressed($event, job)"
                   (keyup)="jobKeyRelease($event, job)"
                   *ngFor="let job of jobs; let first = first; index as i"
                   tabindex="{{i + 2}}"
              >

                <!-- by default use the expanded full detail/actions layout -->

                <div *ngIf="!compact; else compactlist">

                  <!-- layout for this job -->

                  <div class="simulator-select-colum">
                    <p-checkbox
                      [inputId]="'job-' + job.simKey()"
                      name="simselect"
                      [value]="job.simKey()"
                      [(ngModel)]="selection"
                      (onChange)="selectionUpdate()"
                    />
                  </div>

                  <!-- status / progress summary -->

                  <div class="simulator-progress-colum">

                    <!-- the overall status icon -->

                    <img
                      alt="{{SimulationJob.statusString(job.status).toUpperCase()}}"
                      title="{{SimulationJob.statusString(job.status).toUpperCase()}}"
                      src="/assets/images/{{imageForStatus(job.status)}}">

                    <!-- if still active, show the progress -->

                    <span *ngIf="!job.resolved" class="simulator-progress-value">

                      {{ job.progress.toFixed(0) }}%

                    </span>


                  </div>

                  <!-- which simulator tag, so they know which simulator the job is from -->

                  <div class="simulator-tag-colum" [ngSwitch]="job.config.engine">
                    <p-tag title="linesim" severity="success" *ngSwitchCase="'linesim'" value="SIM"/>
                    <p-tag title="linesimbeta" severity="warn" *ngSwitchCase="'linesimbeta'" value="BETA"/>
                    <p-tag title="cloudsim" severity="info" *ngSwitchCase="'cloudsim'" value="CLOUD"/>
                  </div>

                  <!-- simulation name along with the most recent simulation error (if there is one) -->

                  <div class="simulator-name-colum">

                    <!-- human readable name for simulation -->
                    <div class="simulator-name-colum-name-row">
                      {{ job.nameFormatted() }}
                    </div>

                    <!-- key statistics -->
                    <div class="simulator-name-colum-stats-row">
                      <span style="font-weight: bold; color: #a8a8a8">{{ job.jobId }}</span> -
                      <span style="font-weight: bold; color: #a8a8a8">{{ ago(job.updated, 2, lastFetchTime) }}</span> -
                      [<span
                      style="font-weight: bold; color: #a8a8a8">{{ job.simulator() }}</span>]
                      <span style="font-weight: bold; color: #a8a8a8"> RT F: {{(job.details.freight_reaction_time / 60.0).toFixed(1)}} min P: {{(job.details.passenger_reaction_time / 60.0).toFixed(1)}} min </span>
                      <span>{{ job.statsFormatted() }}</span>
                    </div>

                    <!-- most recent error if there is one -->
                    <div class="simulator-name-colum-error-row">
                      {{ job.errorFormatted() }}
                    </div>

                  </div>

                  <!-- when it was last updated -->

                  <div class="simulator-ago-colum">
                    {{ job.updatedFormatted() }}
                  </div>

                  <!-- artifact downloads -->

                  <div class="simulator-artifacts-colum">

                    <!-- give them a quick action to send this simulation to the string line viewer -->

                    <div class="simulator-artifact-button-wrapper"
                         [class.simulator-artifact-missing]="!job.complete()">
                      <i
                        style="color: white;" class="simple-label fas fa-train"
                        title="View String Line"
                        [class.simulator-artifact-missing]="!job.complete()"
                        [class.simulator-not-resolved]="!job.complete()"
                        (click)="stringLineView(job); $event.stopPropagation();">
                      </i>
                    </div>

                    <!--

                    for each possible artifact, we have to show an unavailable icon if we don't have it,
                    or if we do have it, the normal clickable icon, that will lead to downloading that
                    artifact.

                    -->

                    <div
                      class="simulator-artifact-button-wrapper"
                      [class.simulator-artifact-missing]="artifact.url == ''"
                      *ngFor="let artifact of job.artifacts()">

                      <img
                        [class.simulator-artifact-missing]="artifact.url == ''"
                        (mousedown)="$event.stopPropagation()"
                        (mouseup)="$event.stopPropagation()"
                        (click)="clickToDownload($event, job, artifact.kind, artifact.url == '');"
                        title="{{job.artifactTitle(artifact.kind)}}"
                        src="{{job.iconOfArtifact(artifact.kind, artifact.url == '')}}">

                    </div>

                    <!-- give them a quick action to delete the remote simulation -->

                    <div class="simulator-artifact-button-wrapper">
                      <i
                        style="color: white;" class="simple-label fas fa-trash"
                        title="Delete Job"
                        [class.simulator-artifact-missing]="false"
                        [class.simulator-not-resolved]="false"
                        (click)="deleteJobs([job]); $event.stopPropagation();">
                      </i>

                    </div>

                  </div>

                  <!-- end of row -->

                </div> <!-- end of default layout -->

                <!-- the alternate compact layout -->

                <ng-template #compactlist>

                  <!-- layout for this job -->

                  <!-- status / progress summary -->

                  <div class="simulator-progress-colum" *ngIf="!job.active(); else activejobstatus">

                    <!-- the overall status icon -->

                    <img
                      alt="{{SimulationJob.statusString(job.status).toUpperCase()}}"
                      title="{{SimulationJob.statusString(job.status).toUpperCase()}}"
                      src="/assets/images/{{imageForStatus(job.status)}}">

                  </div>

                  <ng-template #activejobstatus>

                    <div class="simulator-progress-colum">
                      <span>
                        {{ job.progress.toFixed(0) }}%
                      </span>
                    </div>

                  </ng-template>

                  <!-- which simulator tag, so they know which simulator the job is from -->

                  <div class="simulator-tag-colum" [ngSwitch]="job.config.engine">
                    <p-tag title="linesim" severity="success" *ngSwitchCase="'linesim'" value="S"/>
                    <p-tag title="linesimbeta" severity="warn" *ngSwitchCase="'linesimbeta'" value="B"/>
                    <p-tag title="cloudsim" severity="info" *ngSwitchCase="'cloudsim'" value="C"/>
                  </div>

                  <!-- quick access to the most frequent actions -->

                  <!-- launch the string line view -->

                  <div class="simulator-artifact-button-wrapper"
                       [class.simulator-artifact-missing]="!job.complete()">
                    <i
                      style="color: white;" class="simple-label fas fa-train"
                      title="View String Line"
                      [class.simulator-artifact-missing]="!job.complete()"
                      [class.simulator-not-resolved]="!job.complete()"
                      (click)="stringLineView(job); $event.stopPropagation();">
                    </i>
                  </div>

                  <!-- delete the simulation -->

                  <div class="simulator-artifact-button-wrapper">
                    <i
                      style="color: white;" class="simple-label fas fa-trash"
                      title="Delete Job"
                      [class.simulator-artifact-missing]="false"
                      [class.simulator-not-resolved]="false"
                      (click)="deleteJobs([job]); $event.stopPropagation();">
                    </i>

                  </div>

                  <!-- the simulation ID -->

                  <span title="Job ID: {{job.jobId}}, Client ID: {{job.details.client_id }}" style="font-weight: bold; color: #a8a8a8; width: 150px; display: inline-block;">
                    {{ job.jobId }} / {{ job.details.client_id }}
                  </span>

                  <!-- the simulation date -->

                  <span title="{{job.updated.toString().replace('T', ' ')}}" style="font-weight: bold; color: #a8a8a8; width: 170px; display: inline-block;">
                    {{ ago(job.updated, 2, lastFetchTime) }}
                  </span>

                  <!-- the simulation name -->

                  <div
                    class="simulator-name-colum-name-row"
                    [class.simulation-had-errors]="job.errorFormatted().trim() != ''"
                    [title]="job.errorFormatted()"
                  >
                    {{ job.nameFormatted() }}
                  </div>

                  <!-- end of row -->

                </ng-template>

              </div> <!-- end of list item -->

            </div> <!-- end of job viewer wrapper -->

          </ng-template>

        </p-dataView>


      </ul>

      <!-- status panel for the remote simulation engines -->

      <div class="jobs-viewer-status">

        <!-- compact layout mode -->

        <div id="jobs-viewer-layout-button-wrapper" class="jobs-viewer-layout-button-wrapper">
          <p-button
            (click)="compactModeToggle()"
            class="jobs-viewer-layout-button"
            icon="fa-solid fa-down-left-and-up-right-to-center"
            title="Toggle compact and normal layout modes. In compact mode, Right Click for action menu."
          />
        </div>

        <!-- selection details -->

        <div id="jobs-viewer-selection-report" class="jobs-viewer-selection-report">

          <div *ngIf="selection.length == 0; else selectionreport">

            <span>No Simulations Selected.</span>

          </div>

          <ng-template #selectionreport>

            <span>{{selection.length}} Selected</span>

          </ng-template>

        </div>

        <!--
        for each simulation engine, show a block that gives a up/down status light, the
        the engine label and any summary status.

        rather than just showing the label for each one, we show the check box (which
        is also labeled) for enabling / disabling each one in the job list.  This
        allows us to save some real-estate in the GUI, and it places the checkboxes
        semantically closer to their concept.

        -->
        <div *ngFor="let simulator of simulators" class="jobs-viewer-status-engine">

          <p-avatar
            pBadge
            image="{{engineStatusImage(engineStatus[simulator.endpoint])}}"
            size="normal"
            badgeSize="large"/>

          <p-checkbox
            (onChange)="simulatorsChanged()"
            [title]="simulator.title"
            [(ngModel)]="selectedSimulators"
            name="group"
            [value]="simulator.endpoint"
            class="simulator-engine-filter-checkbox"
          />
          <label class="simulator-engine-filter-label">{{simulator.name}}</label>
        </div>
      </div>

    </div>
  `
})

export class JobsViewerComponent implements AfterViewInit, OnDestroy, OnInit {

  /** the bag of all jobs we know about */
  public jobMap: Map<string, SimulationJob> = new Map<string, SimulationJob>();

  /** the same bag of jobs, but ordered for whatever way we like to order them */
  public jobList: SimulationJob[] = [];

  /** the sub-set of jobs that are known to be running right now. */
  public runningJobs: SimulationJob[] = [];

  /**
   * we track deleted jobs, just in case there is timing where a job was deleted,
   * but the simulations table has not been updated yet, and we do a fetch.
   *
   */
  public deletedJobs: SimulationJob[] = [];

  /** the files we need to upload() (launch simulations for) */
  private pendingFiles: File[]     = [];

  /** the timer id for the next time fetchJobs() will run */
  private fetchTimerId: number          = -1;

  /** the timer id for the next simulation engine health check */
  private pingTimerId: number           = -1;

  /** how we are filtering the recent jobs list (by sub-string) */
  public searchFilter: string           = '';

  /** the current sort mode */
  public sortMode: string               = 'dateUp';

  /** the current pattern matching mode */
  public matchMode: string              = 'substring';

  /** are in compact layout display mode? */
  public compact: boolean = false;

  /** the last time we fetched the simulations */
  public lastFetchTime: number          = 0;

  /** the modes we have for searching */
  public searchModes: any[]             = [
    {
      value: 'substring',
      name: 'substring',
      icon: 'fa-solid fa-terminal',
      title: 'Enter a partial name (case insensitive)'
    },
    {
      value: 'exact',
      name: 'exact',
      icon: 'fa-solid fa-quote-left',
      title: 'Must an exact Match (case insensitive)'
    },
    {
      value: 'simple',
      name: 'simple',
      icon: 'fa-solid fa-star-of-life',
      title: 'Use (*) as a wild card (case insensitive)'
    },
    {
      value: 'regex',
      name: 'regex',
      icon: 'fa-solid fa-r',
      title: 'POSIX Regular Expression Matching (case insensitive)'
    }
  ];

  /** the current page of results we are viewing */
  public pageCursor: number             = 1;

  /** the row position for paginator */
  public firstRow: number              = 0;

  /** what page sizes to allow */
  public sizeOptions: number[] = [5, 20, 40, 60, 80, 100];

  /** the first usable page of results */
  public startPage: number              = 1;

  /** the last page of results */
  public endPage: number                = 1;

  /** the total row count, accounting for any filtering */
  public totalRows: number              = 0;

  /** the current page size */
  public pageSize: number               = 20;

  /** flag for when we shouldn't allow user actions */
  public busy: boolean                  = false;

  /** the current selection (which checkboxes are ticked on current page) */
  public selection: string[]            = [];

  /** the current job position (click/keyboard) if there is one. */
  public jobCursor: string              = '';

  /** the previous place we clicked with the mouse */
  public previousCursor: string         = '';

  /** are they holding down the ctrl key? */
  public holdingCtrl: boolean           = false;

  /** are they holding down the shift key? */
  public holdingShift: boolean           = false;

  /** the selected date range to filter by */
  public rangeDates: (Date|null)[]|null = [];
  public lastRange: (Date|null)[]|null = [];

  /** the set of available simulation engines to work with.. */
  public simulators: {name: string, title: string, endpoint: string}[]  = [
    { name: 'CLOUD', title: 'Cloud Run (CLOUD)',     endpoint: 'cloudsim'},
    { name: 'SIM',   title: 'Line Simulator (SIM)',  endpoint: 'linesim'},
    { name: 'BETA',  title: 'Line Simulator (BETA)', endpoint: 'linesimbeta'}
  ];

  public engineStatus: {cloudsim: string, linesim: string, linesimbeta: string} = {
    cloudsim:    'unknown',
    linesim:     'unknown',
    linesimbeta: 'unknown'
  };

  /** simulators that are enabled by default */
  public selectedSimulators: string[] = ['cloudsim', 'linesim', 'linesimbeta'];

  /** the dataview's API */
  @ViewChild('dv') dataView: DataView;

  /** standard constructor */
  constructor(
    private     ide:     IdeService,
    private blobber:     BlobService,
    private  modals:     ModalService,
    private linesim:     LineSimService,
    private watcher:     JobWatcherService,
    private taskMonitor: TaskProgressService
  ) {

    Logger.debug('[jobsviewer] dynamically creating...');

    this.lastFetchTime = Date.now();

  }

  /** override any current selection, with selection of everything on the current page */
  selectAllOnPage(): void {

    const jvc: JobsViewerComponent = this;

    Logger.debug(`[jobsviewer] selecting all on page...`);

    /*
     * for any selected remote job, its checkbox ID is expected to be of the form:
     *
     *   linesim-job-cloudsim-12
     *
     * that is, the value from:
     *
     *   job.simKey()
     *
     * So, we have to walk the current page of results, and collect up all of those values,
     * and place them in 'selection'.
     *
     */

    jvc.selection = jvc.jobList.map(x => x.simKey());

    jvc.selectionUpdate();
  }

  /** clear out the current selection (uncheck everything that is checked) */
  clearSelection(): void {

    const jvc: JobsViewerComponent = this;

    Logger.debug(`[jobsviewer] clearing selection...`);

    jvc.selection = [];

    jvc.selectionUpdate();
  }

  /** delete the selected remote simulation jobs */
  deleteSelected(): void {

    const jvc: JobsViewerComponent = this;

    Logger.debug(`[jobsviewer] deleting jobs in current selection...`);

    jvc.deleteJobs(jvc.jobList.filter(x => jvc.selection.includes(x.simKey()) ));

    jvc.clearSelection();
  }

  /** selection has changed somehow, do any housekeeping... */
  selectionUpdate(): void {

    const jvc: JobsViewerComponent = this;

    Logger.debug(`[jobsviewer] selection updated: `, jvc.selection);




  }

  /** the search mode has been changed */
  searchModeUpdated(event): void {

    const jvc: JobsViewerComponent = this;

    Logger.debug(`[jobsviewer] search mode updated: `, jvc.matchMode);


  }

  /** handle a new date range being set. */
  dateRangeChanged(): void {

    const jvc: JobsViewerComponent = this;

    /*
     * check if we have something new...
     *
     */

    if (!jvc.lastRange) {

      jvc.lastRange = [];
    }

    if (jvc.rangeDates) {

      if (jvc.rangeDates.length == jvc.lastRange.length) {

        /* the count is the same, but are the start end points the same? if they swap start/end...we count it as a change. */

        if (jvc.rangeDates.every((v: Date, i: number): boolean =>
          v.toISOString().split('T')[0] === jvc.lastRange[i].toISOString().split('T')[0])) {

          /* looks the same to us, not actually a change. */

          Logger.debug(`[jobsviewer] date updated, but appears unchanged.`);

          return;
        }
      }

    }

    /* whatever it is, it's not the same as before. */

    jvc.lastRange = jvc.rangeDates;

    /*
     * the date range will be:
     *
     *   null         - (no date range)
     *   [Date, null] - implied single day
     *   [Date, Date] - the start and end days
     *
     */


    Logger.debug(`[jobsviewer] date range changed...`, jvc.lastRange);

    /* new set of results, so set the current page back to first page */

    jvc.firstRow   = 0;
    jvc.pageCursor = 1;

    /* refresh */

    jvc.fetchJobs();

  }

  /** change the sorting mode */
  toggleDateSorting(): void {

    if ((this.sortMode == 'dateDown') || (this.sortMode == 'nameUp') || (this.sortMode == 'nameDown')) {

      this.sortMode = 'dateUp';

    } else {

      this.sortMode = 'dateDown';
    }

    /* schedule a refresh of the list */

    Logger.debug(`[jobsviewer] date sort mode changed...`);

    this.fetchJobs();
  }

  /** change the sorting mode */
  toggleNameSorting(): void {

    if ((this.sortMode == 'nameDown') || (this.sortMode == 'dateUp') || (this.sortMode == 'dataDown')) {

      this.sortMode = 'nameUp';

    } else {

      this.sortMode = 'nameDown';
    }

    /* schedule a refresh of the list */

    Logger.debug(`[jobsviewer] name sort mode changed...`);

    this.fetchJobs();
  }

  compactModeToggle(): void {

    const jvc: JobsViewerComponent = this;

    if (jvc.compact) {

      jvc.compact = false;

    } else {

      jvc.compact = true;
    }

    Logger.debug(`[jobsviewer] compact mode changed...`);


  }

  /** fetch the icon to use for the date sort button */
  iconForDateSort(): string {

    if (this.sortMode == 'dateUp') {

      return 'pi pi-sort-up';

    } else if (this.sortMode == 'dateDown') {

      return 'pi pi-sort-down';
    }

    return '';
  }
  /** fetch the icon to use for the name sort button */
  iconForNameSort(): string {

    if (this.sortMode == 'nameUp') {

      return 'pi pi-sort-up';

    } else if (this.sortMode == 'nameDown') {

      return 'pi pi-sort-down';
    }

    return '';
  }

  ngOnInit(): void {

    const jvc: JobsViewerComponent = this;

    jvc.jobList       = [];
    jvc.searchFilter  = '';
    jvc.sortMode      = 'dateUp';
    jvc.busy          = false;

    this.lastFetchTime = Date.now();

    /* start tracking the jobs from the various simulation engine nodes */

    Logger.debug(`[jobsviewer] ngOnInit starts...`);

    jvc.fetchJobs();

    /* start monitoring the health of the simulators */

    jvc.pingEngines();

    /* watch for updates to active jobs ... */

    Logger.debug(`[jobsviewer] subscribing to job watcher for updates...`);

    jvc.watcher.updates().subscribe((jobs: SimulationJob[]): void => {

      Logger.debug(`[jobsviewer] update from job watcher: `, jobs);

      this.update(jobs, true);

    });

    /* define the context menu (once triggered) */

    $.contextMenu({

      selector:            '.job-viewer-list-item',
      autoHide:            true,
      reposition:          false,
      hideOnSecondTrigger: true,
      zIndex:              100000,
      trigger:             'right',
      className:           'data-title',

      events: {

        show: (options): void => {

          const jobId: string        = $(options.$trigger).data('jobid');
          const   job: SimulationJob = jvc.jobMap.get(jobId);

          //
          // in full screen mode we can only see children of the thing that is full screen, by default context
          // menu is a child of 'body'.  We can't just invoke contextMenu() on the right <div>, because it calls
          // the other contextmenu function already in jQuery.
          //
          //

          const base: string = basename(job.details.name);

          //   $('.data-title').detach().appendTo('.fancytree-container');

          $('.data-title').attr('data-menutitle', base);
        }
      },

      build: ($trigger, e: any) => {

        if (typeof e.originalEvent === 'undefined') {

          return false;
        }

        // select the tree node they right-clicked on

        const   jobId: string        = $($trigger).data('jobid');
        const     job: SimulationJob = jvc.jobMap.get(jobId);

        const canStringLine: boolean = job.complete();
        const     completed: boolean = job.complete();
        let          canLog: boolean = false;
        let       canOutput: boolean = false;

        if (job instanceof SimpleSimJob) {

          canLog    = !empty((job as SimpleSimJob).logDownload);
          canOutput = !empty((job as SimpleSimJob).outputDownload);
        }

        const artifactScenario: boolean = !empty(job.artifactURL('scenario'));
        const artifactMovement: boolean = !empty(job.artifactURL('movement'));
        const artifactCmd:      boolean = !empty(job.artifactURL('cmd'));
        const artifactLog:      boolean = !empty(job.artifactURL('log'));
        const artifactErrors:   boolean = !empty(job.artifactURL('errors'));
        const artifactKPIX:     boolean = !empty(job.artifactURL('kpix'));

        return {

          callback: (key, options): void => {

            const   jobId: string        = $($trigger).data('jobid');
            const     job: SimulationJob = jvc.jobMap.get(jobId);

            switch (key) {

              case 'details':

                /* pop a dialog with fine grained detail about the simulation */

                jvc.viewDetails(job);

                break;

              /* browsing the scenario data in one of our scenario details panels ... */

              case 'stringlineview':

                jvc.stringLineView(job);

                break;

              case 'connections':

                Logger.debug(`[jobsviewer] (connections) - fetching content...`);

                jvc.gatherInputOutput(job,
                  (result?: {scenario: string, movement: string}): void  => {

                    /*
                     * we have the full data of both the scenario and the movement, make a string line viewer
                     * meta descriptor that is suitable for 'direct' mode viewing.
                     *
                     */

                    Logger.debug(`[jobsviewer] (connections) - content, ready, launching connection viewer...`);

                    let scenario: Scenario = new Scenario(JSON.parse(result.scenario), null, null);

                    this.ide.launchWindow(new ConnectionViewerMetadata('', '', scenario));

                  },
                  (error: string): void => {

                    Logger.error(`[jobsviewer] (stringlineview) - can't find inputs (${job.jobId}): `, errorAsText(error));
                  },
                  true);

                break;

              case 'scenariobrowse':

                Logger.debug(`[jobsviewer] (scenariobrowse) - fetching content...`);

                jvc.gatherInputOutput(job,
                  (result?: {scenario: string, movement: string}): void  => {

                    /*
                     * we have the full data of both the scenario and the movement, make a string line viewer
                     * meta descriptor that is suitable for 'direct' mode viewing.
                     *
                     */

                    Logger.debug(`[jobsviewer] (scenariobrowse) - content, ready, launching scenario browser...`);


                    this.ide.launchWindow(new ScenarioBrowserMetadata(
                      '',
                      job.nameFormatted(true),
                      job.details.name,
                      result.scenario
                    ));

                  },
                  (error: string): void => {

                    Logger.error(`[jobsviewer] (scenariobrowse) - can't find inputs (${job.jobId}): `, errorAsText(error));
                  },
                  true);

                break;

              case 'diversions':

                Logger.debug(`[jobsviewer] (diversions) - fetching content...`);

                jvc.gatherInputOutput(job,
                  (result?: {scenario: string, movement: string}): void  => {

                    /*
                     * we have the full data of both the scenario and the movement, make a string line viewer
                     * meta descriptor that is suitable for 'direct' mode viewing.
                     *
                     */

                    Logger.debug(`[jobsviewer] (diversions) - content, ready, launching scenario browser...`);

                    this.ide.launchWindow(new DiversionEditorMetadata(
                      '',
                      job.nameFormatted(true),
                      job.details.name,
                      result.scenario
                    ));

                  },
                  (error: string): void => {

                    Logger.error(`[jobsviewer] (diversions) - can't find inputs (${job.jobId}): `, errorAsText(error));
                  },
                  true);

                break;

              case 'tso':

                Logger.debug(`[jobsviewer] (tso) - fetching content...`);

                jvc.gatherInputOutput(job,
                  (result?: {scenario: string, movement: string}): void  => {

                    /*
                     * we have the full data of both the scenario and the movement, make a string line viewer
                     * meta descriptor that is suitable for 'direct' mode viewing.
                     *
                     */

                    Logger.debug(`[jobsviewer] (tso) - content, ready, launching scenario browser...`);

                    this.ide.launchWindow(new TSOEditorMetadata(
                      '',
                      job.nameFormatted(true),
                      job.details.name,
                      result.scenario
                    ));

                  },
                  (error: string): void => {

                    Logger.error(`[jobsviewer] (tso) - can't find inputs (${job.jobId}): `, errorAsText(error));
                  },
                  true);

                break;

              case 'workblocks':

                Logger.debug(`[jobsviewer] (workblocks) - fetching content...`);

                jvc.gatherInputOutput(job,
                  (result?: {scenario: string, movement: string}): void  => {

                    /*
                     * we have the full data of both the scenario and the movement, make a string line viewer
                     * meta descriptor that is suitable for 'direct' mode viewing.
                     *
                     */

                    Logger.debug(`[jobsviewer] (workblocks) - content, ready, launching scenario browser...`);

                    this.ide.launchWindow(new WorkBloxEditorMetadata(
                      '',
                      job.nameFormatted(true),
                      job.details.name,
                      result.scenario
                    ));

                  },
                  (error: string): void => {

                    Logger.error(`[jobsviewer] (tso) - can't find inputs (${job.jobId}): `, errorAsText(error));
                  },
                  true);

                break;

              /* viewing one of the artifacts...  */

              case 'viewScenario':
                jvc.artifactView(job, 'scenario', 'json');
                break;

              case 'viewMovement':
                jvc.artifactView(job, 'movement', 'json');
                break;

              case 'viewKPI':
                jvc.artifactView(job, 'kpi', 'json');
                break;

              case 'viewKPIX':
                jvc.artifactView(job, 'kpix', 'json');
                break;

              case 'viewLog':
                jvc.artifactView(job, 'log', 'text');
                break;

              case 'viewErrors':
                jvc.artifactView(job, 'errors', 'text');
                break;

              /* downloading one of the artifacts...  */

              case 'downloadScenario':
                jvc.artifactDownload(job, 'scenario', false);
                break;

              case 'downloadMovement':
                jvc.artifactDownload(job, 'movement', false);
                break;

              case 'downloadKPI':
                jvc.artifactDownload(job, 'kpi', false);
                break;

              case 'downloadKPIX':
                jvc.artifactDownload(job, 'kpix', false);
                break;

              case 'downloadLog':
                jvc.artifactDownload(job, 'log', false);
                break;

              case 'downloadErrors':
                jvc.artifactDownload(job, 'errors', false);
                break;

            }
          },

          items: {

            scenarioSub: {
              name: 'Scenario...',
              items: {
                stringlineview: {
                  name:     'String Line Viewer',
                  icon:     'fas fa-train',
                  disabled: !artifactScenario || !artifactMovement
                },
                connections: {
                  name:     'Connections',
                  icon:     'fas fa-project-diagram',
                  disabled: !artifactScenario
                },
                scenariobrowse: {
                  name:     'Browse',
                  icon:     'fas fa-table',
                  disabled: !artifactScenario
                },
                diversions: {
                  name:     'Diversions',
                  icon:     'fas fa-route',
                  disabled: !artifactScenario
                },
                tso: {
                  name:     'TSO',
                  icon:     'fas fa-tools',
                  disabled: !artifactScenario
                },
                workblocks: {
                  name:     'Workblocks',
                  icon:     'fas fa-snowplow',
                  disabled: !artifactScenario
                }
              }
            },
            details:     {name: 'Details',  icon: 'fas fa-info',          disabled: !completed},
            viewSub: {
              name: 'View...',
              items: {
                viewScenario: {
                  name:     'Scenario',
                  icon:     'fas fa-keyboard',
                  disabled: !artifactScenario
                },
                viewMovement: {
                  name:     'Movement',
                  icon:     'fas fa-keyboard',
                  disabled: !artifactMovement
                },
                viewKPIX: {
                  name:     'KPIX',
                  icon:     'fas fa-keyboard',
                  disabled: !artifactKPIX
                },
                viewLog: {
                  name:     'Simulation Log',
                  icon:     'fas fa-keyboard',
                  disabled: !artifactLog
                },
                viewErrors: {
                  name:     'Simulation Errors',
                  icon:     'fas fa-keyboard',
                  disabled: !artifactErrors
                }
              }
            },
            downloadSub: {
              name: 'Download...',
              items: {
                downloadScenario: {
                  name:     'Scenario',
                  icon:     'fas fa-file-download',
                  disabled: !artifactScenario
                },
                downloadMovement: {
                  name:     'Movement',
                  icon:     'fas fa-file-download',
                  disabled: !artifactMovement
                },
                downloadKPIX: {
                  name:     'KPIX',
                  icon:     'fas fa-file-download',
                  disabled: !artifactKPIX
                },
                downloadLog: {
                  name:     'Simulation Log',
                  icon:     'fas fa-file-download',
                  disabled: !artifactLog
                },
                downloadErrors: {
                  name:     'Simulation Errors',
                  icon:     'fas fa-file-download',
                  disabled: !artifactErrors
                }
              }
            }
          }
        }

      }

    });

    /* - - - - - -  add in drag n' drop behaviors */

    /* get the drop box */

    const dropArea: HTMLElement = $('.jobs-scenario-drop-zone').get(0);

    /* prevent bubble up, we're handling drag n' drop for this area */

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName: string): void => {

      dropArea.addEventListener(eventName, (e) => {

        e.preventDefault();
        e.stopPropagation();

      }, false);

    });

    ['dragenter', 'dragover'].forEach((eventName: string): void => {
      dropArea.addEventListener(eventName, (e): void => {

        $(dropArea).addClass('highlight');

      }, false);

    });

    ['dragleave', 'drop'].forEach((eventName: string): void => {
      dropArea.addEventListener(eventName, (e) => {

        $(dropArea).removeClass('highlight');

      }, false);

    });

    dropArea.addEventListener('drop', (e) => {

      const           dt: DataTransfer = e.dataTransfer;
      this.pendingFiles  = Array.from(dt.files);

      this.upload();

    }, false);

    $('input#jobsFileBrowser[type="file"]').on('change', (e): void => {

      jvc.pendingFiles = Array.from((e.target as HTMLInputElement).files);

      jvc.upload();

    });

    /* setup is complete */

    Logger.debug(`[jobsviewer] ready.`);

  }

  ngAfterViewInit(): void {

    const jvc: JobsViewerComponent = this;


  }

  isCursorPosition(job: SimulationJob): boolean {

    let jvc: JobsViewerComponent = this;

    return jvc.jobCursor == job.simKey();
  }

  isSelected(job: SimulationJob): boolean {

    let jvc: JobsViewerComponent = this;

    return jvc.selection.includes(job.simKey());
  }

  /** check if a job is ready to be exported to other areas of the GUI */
  jobIsDraggable(job: SimulationJob): boolean {

    /* basically the job just have completed, one way or the other. */

    return job.resolved;

  }

  /**
   * we want to allow users to drag jobs out of the job view, and do things like "import" into the
   * project manager
   *
   * @param event the drag event
   * @param job   the job they are trying to drag
   *
   */
  startDrag(event: DragEvent, job: SimulationJob): void {

    Logger.debug(`[jobsviewer] (startDrag) - building job transfer (${job.jobId}):`, job);

    /* serialize so we can pass through a drag and drop data transfer ... */

    let data: string = JSON.stringify(job.toJSON());
    event.dataTransfer.setData("job", data);

    Logger.debug(`[jobsviewer] (startDrag) - transfer 'job' set to:`, data);

  }

  /**
   * Clicking on a row (one of the simulations) implicitly means working with the
   * selection. We allow for CTRL and SHIFT keys etc. to be used in combination with
   * mouse clicking so they can manage the selection.
   *
   */
  jobClicked(event: MouseEvent, job: SimulationJob): void {

    let jvc: JobsViewerComponent = this;

    let ctrl: boolean            = event.ctrlKey;
    let shift: boolean           = event.shiftKey;

    jvc.previousCursor    = jvc.jobCursor;

    event.preventDefault();
    event.stopPropagation();

    $(event.currentTarget).trigger('focus');

    jvc.jobCursor = job.simKey();

    /* Logger.debug(`[jobsviewer] (clicked) - ctrl=${ctrl}, shift=${shift}, job:`, job); */

    if (ctrl && shift) {

      /*
       * if they use SHIFT + click, it means to extend the selection to include everything from the
       * cursor to (and including) the thing they clicked on.
       *
       */

      if (jvc.previousCursor == jvc.jobCursor) {

        /* this item is the selection */

        jvc.selection = [jvc.jobCursor];

        jvc.selectionUpdate();

        return ;
      }

      /* we need to add to selection everything BETWEEN previousCursor and jvc.jobCursor ... */

      let a: number = jvc.jobList.findIndex(x => x.simKey() == jvc.previousCursor);
      let b: number = jvc.jobList.findIndex(x => x.simKey() == jvc.jobCursor);

      if (b < a) {

        /* make sure a is always 'start' and b is always 'end' */

        [a, b] = [b, a];
      }

      let toAppend: string[] = jvc.jobList.slice(a, b + 1).map(x => x.simKey());
      jvc.selection          = [...jvc.selection.filter(x => !toAppend.includes(x))];
      jvc.selection          = [...jvc.selection, ...toAppend];

    } else if (ctrl) {

      /* if they use CTRL + click, it means toggle selection status of ONLY the thing they clicked on */

      /*
       * if it's in the selection, remove it from the selection.  if not in the selection, then add it do the
       * selection.
       *
       */

      if (jvc.selection.includes(jvc.jobCursor)) {

        /* remove */

        jvc.selection = jvc.selection.filter(x => x != jvc.jobCursor);

      } else {

        /* add */

        jvc.selection = [...jvc.selection, jvc.jobCursor];
      }

    } else {

      /* clicking an item makes it the current position, and replaces the current selection */

      jvc.selection = [jvc.jobCursor];
    }


    jvc.selectionUpdate();
  }

  jobKeyRelease(event: KeyboardEvent, job: SimulationJob): void {

    let jvc: JobsViewerComponent = this;
    let ctrl: boolean  = event.ctrlKey;
    let key: string    = event.key.toLowerCase();

    event.preventDefault();
    event.stopPropagation();

    if (key == 'control') {

      Logger.debug(`[jobsviewer] released ctrl ...`);

      jvc.holdingCtrl = false;
    }

    if (key == 'shift') {

      Logger.debug(`[jobsviewer] released shift ...`);

      jvc.holdingShift = false;
    }
  }

  jobKeyPressed(event: KeyboardEvent, job: SimulationJob): void {

    let jvc: JobsViewerComponent = this;

    let ctrl: boolean  = event.ctrlKey;
    let shift: boolean = event.shiftKey;
    let key: string    = event.key.toLowerCase();

    event.preventDefault();
    event.stopPropagation();

    if (key == 'control') {

      Logger.debug(`[jobsviewer] holding ctrl ...`);

      jvc.holdingCtrl = true;
    }

    if (key == 'shift') {

      Logger.debug(`[jobsviewer] holding shift ...`);

      jvc.holdingShift = true;
    }

    /* Logger.debug(`[jobsviewer] (key down) - ctrl=${ctrl}, shift=${shift}, key=${key}, job:`, job); */

    if (key == 'a' && jvc.holdingCtrl) {

      /* CTRL-A means select all on page */

      jvc.selection = jvc.jobList.map(x => x.simKey());

      jvc.selectionUpdate();

    } else if (key == 'r' && jvc.holdingCtrl) {

      /* CTRL-R means refresh the page */

      jvc.fetchJobs();

    }  else if (key == ' ' && jvc.holdingCtrl && jvc.holdingShift) {

      /* SPACE + CTRL + SHIFT means extend selection, just like CTRL + SHIFT + CLICK */

      if (jvc.previousCursor == jvc.jobCursor) {

        /* this item is the selection */

        jvc.selection = [jvc.jobCursor];

        jvc.selectionUpdate();

        return ;
      }

      /* we need to add to selection everything BETWEEN previousCursor and jvc.jobCursor ... */

      let a: number = jvc.jobList.findIndex(x => x.simKey() == jvc.previousCursor);
      let b: number = jvc.jobList.findIndex(x => x.simKey() == jvc.jobCursor);

      if (b < a) {

        /* make sure a is always 'start' and b is always 'end' */

        [a, b] = [b, a];
      }

      let toAppend: string[] = jvc.jobList.slice(a, b + 1).map(x => x.simKey());
      jvc.selection          = [...jvc.selection.filter(x => !toAppend.includes(x))];
      jvc.selection          = [...jvc.selection, ...toAppend];

      jvc.selectionUpdate();

    } else if (key == ' ' && jvc.holdingCtrl) {

      /* SPACE + CTRL means the same as CTRL + click; just toggle the item we are on */

      /*
      * if it's in the selection, remove it from the selection.  if not in the selection, then add it do the
      * selection.
      *
      */

      if (jvc.selection.includes(jvc.jobCursor)) {

        /* remove */

        jvc.selection = jvc.selection.filter(x => x != jvc.jobCursor);

      } else {

        /* add */

        jvc.selection = [...jvc.selection, jvc.jobCursor];
      }

      jvc.selectionUpdate();

    } else if (key == ' ') {

      /* SPACE means clear the selection (whatever it is) */

      jvc.selection = [];

      jvc.selectionUpdate();

    } else if (key == 'enter') {

      Logger.debug(`[jobsviewer] string view on ${jvc.jobCursor}`);

      if (jvc.jobCursor != '') {

        jvc.stringLineView(jvc.jobOf(jvc.jobCursor));
      }

    } else if (key == 'delete') {

      Logger.debug(`[jobsviewer] delete on ${jvc.jobCursor}`);

      if (jvc.jobCursor != '') {

        jvc.deleteJobs([jvc.jobOf(jvc.jobCursor)]);
      }

    } else if (key == 'arrowup') {

      /* move the job cursor up in the list */

      Logger.debug(`[jobsviewer] move up...`);

      let prev: string = jvc.jobKeyBefore(jvc.jobCursor);

      if (prev) {

        jvc.jobCursor = prev;

        $(jvc.jobElement(jvc.jobCursor)).trigger('focus');

        return ;
      }

      /*
       * if we can't move up, it might be because we are at the top of the page; if so
       * we can try to go to the bottom of the previous page.
       *
       * // TODO:
       *
       */

    } else if (key == 'arrowdown') {

      /* move the job cursor down in the list */

      Logger.debug(`[jobsviewer] move down...`);


      let next: string = jvc.jobKeyAfter(jvc.jobCursor);

      if (next) {

        jvc.jobCursor = next;

        $(jvc.jobElement(jvc.jobCursor)).trigger('focus');

        return ;
      }

      /*
       * if we can't move down, it might be because we are at the bottom of the page; if so
       * we can try to go to the top of the next page.
       *
       * // TODO:
       *
       */

    }

  }

  /** what job is lower down the list after this one? */
  jobKeyAfter(jobKey: string): string|null {

    let jvc: JobsViewerComponent = this;

    let pos: number = jvc.jobList.findIndex(x => jobKey == x.simKey());

    if (pos < 0) {

      return null;
    }

    if (pos >= (jvc.jobList.length - 1)) {

      return null;
    }

    return jvc.jobList[pos + 1].simKey();
  }

  /** what job is higher up the list before this one? */
  jobKeyBefore(jobKey: string): string|null {

    let jvc: JobsViewerComponent = this;

    let pos: number = jvc.jobList.findIndex(x => jobKey == x.simKey());

    if (pos < 1) {

      return null;
    }

    return jvc.jobList[pos - 1].simKey();
  }

  /** is this the first job on the page? */
  isFirstJob(jobKey: string): boolean {

    let jvc: JobsViewerComponent = this;

    return jobKey == jvc.jobList[0].simKey();
  }

  /** is this the last job on the page? */
  isLastJob(jobKey: string): boolean {

    let jvc: JobsViewerComponent = this;

    return jobKey == jvc.jobList[jvc.jobList.length - 1].simKey();
  }

  /** convert a job's key to its DOM element */
  jobElement(jobKey: string): HTMLElement|null {

    let hits:  JQuery<HTMLElement> = $("p-dataView").find(`[data-jobid='${jobKey}']`);

    if (hits.length == 0) {

      return null;
    }

    return hits.get(0);
  }

  /** convert a job key back into its job (but only for the current page) */
  jobOf(jobKey: string): SimulationJob|null {

    let jvc: JobsViewerComponent = this;

    let hit: SimulationJob = jvc.jobList.find(x => x.simKey() == jobKey);

    if (!hit) {

      return null;
    }

    return hit;
  }

  /**
   * helper to fetch the content of both the scenario and resulting movement, and pass the
   * full data back to us, so we can then use that...to pass to the string line viewer window
   * etc.
   *
   * @param job          - the job we want the content for
   * @param resolve      - handler to call once we have the expanded content for both input and output
   * @param reject       - handler to call if there is an error
   * @param scenarioOnly - just fetch the scenario
   */
  gatherInputOutput(job: SimulationJob,
                    resolve: (result?: {scenario: string, movement: string})  => void,
                    reject:  (error: string) => void,
                    scenarioOnly: boolean = false): void {

    let jvc: JobsViewerComponent = this;
    let scenarioURL: string      = job.artifactURL('scenario');
    let movementURL: string      = job.artifactURL('movement');

    Logger.debug(`[jobsviewer] gatherInputOutput - scenario=${scenarioURL}, movement=${movementURL} ... `);

    const updateId: string = TaskUpdate.generateId();

    let update: TaskArtifactFetch = new TaskArtifactFetch(updateId, 'Streaming Scenario Google ...', 0, scenarioURL);
    jvc.taskMonitor.progress(update);

    if (scenarioURL == '') {

      let msg: string = `can not find scenario artifact for job ${job.jobId}`;
      let fail: TaskArtifactFetchFailed = new TaskArtifactFetchFailed(updateId, msg);

      jvc.taskMonitor.progress(fail);

      /* no such scenario exists */

      reject(msg);

      return ;
    }

    if (movementURL == '') {

      let msg: string = `can not find movement artifact for job ${job.jobId}`;
      let fail: TaskArtifactFetchFailed = new TaskArtifactFetchFailed(updateId, msg);

      jvc.taskMonitor.progress(fail);

      /* no such movement exists */

      reject(msg);

      return ;
    }

    /* grab the scenario data ... */

    jvc.linesim.artifactContents(scenarioURL,  'text')
      .pipe(first())
      .subscribe({

        next: (scenarioContent: any): void => {

          if (scenarioOnly) {

            Logger.debug(`[jobsviewer] gatherInputOutput - scenario received (${job.jobId}), resolving...`);

            let complete: TaskArtifactFetchComplete = new TaskArtifactFetchComplete(updateId, 'Ready.');

            jvc.taskMonitor.progress(complete);

            resolve({scenario: scenarioContent, movement: ''});

            return ;
          }

          Logger.debug(`[jobsviewer] gatherInputOutput - scenario received (${job.jobId}), fetching movement...`);

          /* grab the movement data ... */

          let update: TaskArtifactFetch = new TaskArtifactFetch(updateId, 'Streaming Movement from Google...', 0, movementURL);
          jvc.taskMonitor.progress(update);

          jvc.linesim.artifactContents(movementURL,  'text')
            .pipe(first())
            .subscribe({

              next: (movementContent: any): void => {

                Logger.debug(`[jobsviewer] gatherInputOutput - movement received (${job.jobId}), resolving...`);

                let complete: TaskArtifactFetchComplete = new TaskArtifactFetchComplete(updateId, 'Ready.');

                jvc.taskMonitor.progress(complete);

                resolve({scenario: scenarioContent, movement: movementContent});

              },
              error: (err): void => {

                let msg: string = errorAsText(err);
                let fail: TaskArtifactFetchFailed = new TaskArtifactFetchFailed(updateId, msg);

                jvc.taskMonitor.progress(fail);

                reject(msg);
              }

            });

        },
        error: (err): void => {

          let msg: string = errorAsText(err);
          let fail: TaskArtifactFetchFailed = new TaskArtifactFetchFailed(updateId, msg);

          jvc.taskMonitor.progress(fail);

          reject(msg);
        }

      });
  }

  /** health check on the simulation engines */
  pingEngines(): void {

    let jvc: JobsViewerComponent = this;

    if (jvc.pingTimerId > 0) {

      Logger.debug(`[jobsviewer] pingEngines - clearing timeout...`);

      window.clearTimeout(jvc.pingTimerId);

      jvc.pingTimerId = -1;
    }

    /* for each engine, build an observable that will do a quick ping ... */

    const obs: any[]    = jvc.simulators.map(x => jvc.linesim.ping(SimConfig.endpointOfEngine(x.endpoint), x.endpoint).pipe(catchError((err) => {

      Logger.error(`[jobsviewer] ping error`, err);

      return of(null);
    })));

    /* launch all the pings at once in parallel ... */

    /*
     * NOTE: with forkJoin() if even 1 of them fails, the whole batch fails.  So we have to tap
     * into the results as they come in, and if any come back as an error...then we create a fake
     * result so that we can still see results from the ones that are working.
     *
     */

    let confirmed: string[] = [];

    forkJoin(obs.map(o => o.pipe())).subscribe({

      next: (results: any): void => {

        Logger.debug(`[jobsviewer] pingEngines - ping results: `, results);

        for (let item of results) {

          if (!item) {

            continue;
          }

          if (!isset(item.params)) {

            continue;
          }

          if (!isset(item.params.engine)) {
            continue;
          }

          confirmed.push(item.params.engine);
        }

        for (const engine in jvc.engineStatus) {

          if (confirmed.includes(engine)) {

            /* ping responded ok. */

            jvc.engineStatus[engine] = 'Up';

            continue;
          }

          /* ping failed in some way */

          jvc.engineStatus[engine] = 'Down';
        }

      },
      error: (err): void => {

        /* error handling */

        Logger.error(`[jobsviewer] pingEngines - problem pinging:`, errorAsText(err));
      }

    });


    /* recur...every 30 seconds */

    jvc.pingTimerId = window.setTimeout((): void => {

      this.pingEngines();

    },  1000 * 180);

  }


  /** load simulations a page at a time, we don't cache in the GUI (want to keep memory usage lower) */
  lazyLoad(event: any): void {

    let jvc: JobsViewerComponent = this;

    /* load the job summaries for the given page */

    let newRows: number = event.rows;
    let newPage: number = (event.first / jvc.pageSize) + 1;

    Logger.debug(`[jobsviewer] lazyLoad rows: ${newRows} on page ${newPage}...`);

    jvc.fetchJobs(newPage, newRows);

  }

  /** update ourselves with the job data from the various simulation engine nodes */
  fetchJobs(newPage: number = -1, newRows: number = -1): void {

    let jvc: JobsViewerComponent = this;

    if (jvc.busy) {

      Logger.debug(`[jobsviewer] fetchJobs - already fetching.`);

      return ;
    }

    Logger.debug(`[jobsviewer] fetchJobs - starts...`);

    this.lastFetchTime = Date.now();

    /*
     * if being re-triggered, instead of being called at the end of our timeout, then
     * clear the existing timeout, so we don't get a double hit.
     *
     */

    if (jvc.fetchTimerId > 0) {

      Logger.debug(`[jobsviewer] fetchJobs - clearing timeout...`);

      window.clearTimeout(jvc.fetchTimerId);

      jvc.fetchTimerId = -1;
    }

    /*
     * set up the query parameters...
     *
     */

    let page: number    = 1;
    let limit: number   = 20;
    let sort: string    = 'dateUp';
    let pattern: string = '';
    let match: string   = '';
    let start: string   = '';
    let end: string     = '';

    /* page navigation */

    page  = jvc.pageCursor;
    limit = jvc.pageSize;

    if (newPage > -1) {

      page = newPage;
    }

    if (newRows > -1) {

      limit = newRows;
    }

    /* (ordering) sort mode */

    let sortArg: string = jvc.sortMode.trim().toLowerCase();

    switch (sortArg) {

      case 'dateup':
      case 'datedown':
      case 'nameup':
      case 'namedown':
        sort = sortArg;
        break;
    }

    /* (filtering) search pattern */

    pattern = jvc.searchFilter.trim().toLowerCase();

    /* (filtering) search pattern matching style */

    let matchArg: string = jvc.matchMode.trim().toLowerCase();

    switch (matchArg) {

      case 'substring':
      case 'exact':
      case 'simple':
      case 'regex':
        match = matchArg;
        break;
    }

    /* (filtering) date range */

    if (jvc.rangeDates && jvc.rangeDates.length > 0) {

      let range: (Date|null)[]|null = [...jvc.rangeDates];

      let a: Date = range.shift() ?? null;
      let b: Date = range.shift() ?? null;

      if (a) {

        start = a.toISOString().split('T')[0];
      }

      if (b) {

        end = b.toISOString().split('T')[0];
      }
    }

    Logger.debug(`[jobsviewer] fetchJobs - forking simulations page=${page}, limit=${limit}, sort=${sort}, pattern=${pattern}, match=${match}, start=${start}, end=${end}...`);

    jvc.lock();

    /* when we ask for the simulations, also filter by which remote simulators are enabled  */

    jvc.linesim.simulations(page,limit,sort,pattern,match,start,end, jvc.selectedSimulators).subscribe({

      next: (data: any): void => {

        Logger.debug(`[jobsviewer] fetchJobs - received page: `, data);

        if (typeof data.rows == 'undefined') {

          Logger.error(`[jobsviewer] fetchJobs - bad row data in simulations listing: `, data);

          jvc.unlock();

          return ;
        }

        /* make sure our pagination co-ordinates are up to date */

        jvc.pageCursor = data.page;
        jvc.pageSize   = data.per_page;
        jvc.firstRow   = data.start - 1;
        jvc.totalRows  = data.total_rows;
        jvc.startPage  = data.start;
        jvc.endPage    = data.end;

        /*
         * force pagination to keep up, doesn't seem to always update, but don't do this
         * if we were called by lazyload(), which itself...is called by paging...so infinite
         * loop.
         *
         */

        if (newPage < 0 && newRows < 0) {

          /*
           * ref:
           *
           *   https://stackoverflow.com/questions/57906936/dataview-page-not-set-to-1-after-get-request
           *   https://stackoverflow.com/questions/44773199/get-current-page-and-move-to-specific-page-in-ngprime-datatable
           *
           */

          jvc.dataView.paginate({
            first: jvc.firstRow,
            rows:  jvc.pageSize
          });
        }

        /*
         * this should be the list/set of jobs for each simulator engine.
         * "up-convert" to SimulationJob items and then just push through
         * update() as usual.
         *
         */

        let jobs: SimulationJob[] = [];

        for (let remoteJob of data.rows) {

          /* these jobs are from the database, so the "ID" is a the row id and not the remote simulator job id */

          remoteJob.id = remoteJob.jid;

          let details: RemoteSimJob = new RemoteSimJob(remoteJob);
          let job: SimpleSimJob     = SimpleSimJob.fromRemoteJob(details, remoteJob.engine);

          jobs.push(job);

        }

        /* make sure ordering/filtering are applied */

        jvc.update(jobs);

        /* GUI is ready again ... */

        jvc.unlock();

      },
      error: (err: any): void => {

        jvc.unlock();

        Logger.error(`[jobsviewer] fetchJobs - can not fetch simulations: `, errorAsText(err));
      }

    });

    /* recur... */

    /*
     * every 3  minute we go and fetch the known jobs from the various line simulators.  We don't have
     * to do this frequently at all, because it's not going to generally be changing. Its mostly jobs
     * that already completed or failed. It may sometimes be the case that we fetch the jobs while a
     * give simulator nodes is actually working on simulations...and that's fine, it just means we
     * may get a double update of rendering, once from here, and once from the monitor() machinery.
     *
     * If they delete or expire jobs manually, then we will cancel the timer and re-trigger fetchJobs().
     *
     * Note: we call window.setTimeout() because npm packaging is including Node.js libraries,
     * which also have a setTimeout() function...which returns a Timeout id instead of a number.
     * So to avoid compiler barfing, we use window.setTimeout() to ensure we pick the function
     * we want.
     *
     */

    jvc.fetchTimerId = window.setTimeout((): void => {

      Logger.debug(`[jobsviewer] periodic fetch jobs...`);

      jvc.fetchJobs();

    },  1000 * 15);

  }

  /** [deprecated] update ourselves with the job data from the various simulation engine nodes */
  fetchJobsLegacy(): void {

    let jvc: JobsViewerComponent = this;

    Logger.debug(`[jobsviewer] fetchJobs - starts...`);

    /*
     * if being re-triggered, instead of being called at the end of our timeout, then
     * clear the existing timeout, so we don't get a double hit.
     *
     */

    if (jvc.fetchTimerId > 0) {

      Logger.debug(`[jobsviewer] fetchJobs - clearing timeout...`);

      window.clearTimeout(jvc.fetchTimerId);

      jvc.fetchTimerId = -1;
    }

    /*
     * for each of the line simulation engine nodes, we want to go and fetch the job summaries of
     * all currently known jobs (up to some page limit), and apply any filtering while we are at
     * it.  Once we have the jobs added to the "all jobs" set of jobs...the Angular style model
     * rendering will handle the visuals.  Our job here is just to go and fetch the raw job
     * data from all the simulation engines.
     *
     */

    /* build the observables ... */

    const limit: number       = 20;
    const endpoints: string[] = jvc.simulators.map(x => x.endpoint);
    const obs: any[]          = endpoints.filter(x => jvc.selectedSimulators.includes(x)).map(x => jvc.linesim.recent(SimConfig.endpointOfEngine(x), x, jvc.searchFilter, limit).pipe(catchError((err) => {

      Logger.error(`[jobsviewer] fetch jobs error`, err);

      return of(null);

    })));

    /* run everything in parallel and then harvest the results... */

    if (obs.length == 0) {

      Logger.warning(`[jobsviewer] fetchJobs - no engines yet?`);

      return ;
    }

    /*
     * NOTE: with forkJoin() if even 1 of them fails, the whole batch fails.  So we have to tap
     * into the results as they come in, and if any come back as an error...then we create a fake
     * result so that we can still see results from the ones that are working.
     *
     */

    Logger.debug(`[jobsviewer] fetchJobs - forking recent jobs requests...`);

    jvc.jobList = [];
    jvc.jobMap.clear();

    jvc.lock();

    forkJoin(obs.map(o => o.pipe())).subscribe({

      next: (results: {engine: string, jobs: any[]}[]): void => {

        jvc.unlock();

        /*
         * this should be the list/set of jobs for each simulator engine.
         * "up-convert" to SimulationJob items and then just push through
         * update() as usual.
         *
         */

        let jobs: SimulationJob[] = [];

        for (let jobGroup of results) {

          if (!jobGroup) {

            continue;
          }

          for (let remoteJob of jobGroup.jobs) {

            let details: RemoteSimJob = new RemoteSimJob(remoteJob);
            let job: SimpleSimJob     = SimpleSimJob.fromRemoteJob(details, jobGroup.engine);

            jobs.push(job);

          }
        }

        /* make sure ordering/filtering are applied */

        jvc.update(jobs);

      },

      error: (err): void => {

        /* error handling */

        jvc.unlock();

        Logger.error(`[result] fetchJobs - problem fetching recent jobs:`, errorAsText(err));
      }

    });

    /* recur... */

    /*
     * every 3  minute we go and fetch the known jobs from the various line simulators.  We don't have
     * to do this frequently at all, because it's not going to generally be changing. Its mostly jobs
     * that already completed or failed. It may sometimes be the case that we fetch the jobs while a
     * give simulator nodes is actually working on simulations...and that's fine, it just means we
     * may get a double update of rendering, once from here, and once from the monitor() machinery.
     *
     * If they delete or expire jobs manually, then we will cancel the timer and re-trigger fetchJobs().
     *
     * Note: we call window.setTimeout() because npm packaging is including Node.js libraries,
     * which also have a setTimeout() function...which returns a Timeout id instead of a number.
     * So to avoid compiler barfing, we use window.setTimeout() to ensure we pick the function
     * we want.
     *
     */

    jvc.fetchTimerId = window.setTimeout((): void => {

      Logger.debug(`[jobsviewer] periodic fetch jobs...`);

      jvc.fetchJobs();

    },  1000 * 60 * 3);
  }

  public basename(key: string) {

    let base: string = key;

    if ( empty(base) ) {

      base = key;
    }

    base = key.substring(base.lastIndexOf('/') + 1);

    if (base.lastIndexOf('.') !== -1) {

      base = base.substring(0, base.lastIndexOf('.'));
    }

    return base;
  }

  /** helper to delete the remote job */
  deleteJobs(jobs: SimulationJob[]): void {

    let jvc: JobsViewerComponent = this;

    Logger.debug(`[jobsviewer] (deleteJobs) - doing remote batch delete of: `, jobs);

    /*
     * add these jobs to the deleted list to avoid showing them even if the database hasn't been updated
     * yet.
     *
     */

    jobs.forEach((job: SimulationJob): void => {

      if (!jvc.deletedJobs.find(x => x.simKey() == job.simKey())) {

        jvc.deletedJobs.push(job);
      }

      jvc.selection = [...jvc.selection.filter(x => x != job.simKey())];

    });

    /*
     * make an observer for each request, then launch them all as a forkJoin; we have to configure
     * and run each one individually because they may be from different remote simulators.
     *
     */

    const observables: any[]    = [];
    const failed: any[]         = [];

    for (let job of jobs) {

      Logger.debug(`[jobsviewer] (deleteJobs) - . queueing: `, job);

      let config: SimConfig     = job.config;
      let jid: number           = job.jobId;

      let o: Observable<Object> = jvc.linesim.delete([jid], config).pipe(
        catchError((err , obs) => {

          /* when there is an error, we insert a fake result, so that the forkJoin doesn't abort */

          Logger.debug(`[jobsviewer] (deleteJobs) - ERROR deleting job #${jid}`, err);

          let message = `Problem deleting job #${jid}: `;

          if (isset(err.statusText)) {

            message += err.statusText;
          }

          if (isset(err.error.message)) {

            message += err.error.message;
          }

          failed.push(message);

          Logger.error(`[jobsviewer] (deleteJobs) - failed: `, message);

          return of({key: jid, endpoint: job.config.endpoint, error: err});
        })
      );

      observables.push(o);
    }

    if (observables.length == 0) {

      /* nothing to do */

      Logger.debug(`[jobsviewer] (deleteJobs) - nothing to do.`);

      return ;
    }

    let N: number          = observables.length;
    let n: number          = 0;
    const updateId: string = TaskUpdate.generateId();

    let update: TaskRemoteSimulationDelete = new TaskRemoteSimulationDelete(updateId, 'Deleting simulations...', n, N);
    jvc.taskMonitor.progress(update);

    /* run the batch... */

    /*
     * Note: forkJoin fails overall if any of its items fail.  This is not good for us,
     * since we want at least partial success. So, we have to intercept the results as they come back,
     * and provide a fake null result so we can gracefully handle any artifact that is actually missing.
     *
     */


    Logger.debug(`[jobsviewer] (deleteJobs) - deleting ${N} remote jobs...`);

    forkJoin(observables.map(o => o.pipe(tap((item: any) => {

      /* each tapped item is the result of doing a remote job delete, as they come back, update the progress... */

      /* as the downloads complete, update the element with download progress */

      n++;

      const progress: string = ((n/N) * 100.0).toFixed(2);

      const key     = item.key;
      let   msg    = '';

      Logger.debug(`[jobsviewer] (deleteJobs) - . (${progress}) deleted ${key} - ${item.endpoint}`);

      let update: TaskRemoteSimulationDelete = new TaskRemoteSimulationDelete(updateId, `#${key} (${item.endpoint})`, n, N);
      jvc.taskMonitor.progress(update);

    })))).subscribe({

      next: (results): void => {

        /* finalize the results... */

        Logger.debug('[jobsviewer] (deleteJobs) deletion results: ', results);

        let errors: any[] = [];

        for (let item of results) {

          if (typeof item.error != 'undefined') {

            /* this one failed */

            errors.push(item);

            continue;
          }

        }

        if (errors.length > 0) {

          Logger.error('[jobsviewer] (deleteJobs) some jobs could not be deleted: ', errors);

          let msg: string = errorAsText(`Some remote simulations could not be deleted.`);
          let fail: TaskRemoteSimulationDeleteFailed = new TaskRemoteSimulationDeleteFailed(updateId, msg);
          jvc.taskMonitor.progress(fail);

        } else {

          /* wrap it up! */

          let complete: TaskRemoteSimulationDeleteComplete = new TaskRemoteSimulationDeleteComplete(updateId, 'Simulations deleted.');
          jvc.taskMonitor.progress(complete);

          Logger.debug('[jobsviewer] (deleteJobs) remote simulations successfully deleted.');
        }

        /* schedule an update to the job listings panel, since we just removed at least one item */

        Logger.debug(`[jobsviewer] after delete fetch jobs...`);

        jvc.fetchJobs();

      },
      error: (err): void => {

        let msg: string = errorAsText(err);
        let fail: TaskRemoteSimulationDeleteFailed = new TaskRemoteSimulationDeleteFailed(updateId, msg);
        jvc.taskMonitor.progress(fail);

        Logger.error(`[jobsviewer] (deleteJobs) - can't delete jobs:`, errorAsText(err));
      }

    });

  }

  /** helper to launch the string line view of a simulation and its train movements results */
  stringLineView(job: SimulationJob): void {

    let jvc: JobsViewerComponent = this;

    Logger.debug(`[jobsviewer] (stringlineview) - fetching content...`);

    jvc.gatherInputOutput(job,
      (result?: {scenario: string, movement: string}): void  => {

        /*
         * we have the full data of both the scenario and the movement, make a string line viewer
         * meta descriptor that is suitable for 'direct' mode viewing.
         *
         */

        Logger.debug(`[jobsviewer] (stringlineview) - content, ready, launching string line viewer...`);

        this.ide.launchWindow(
          new StringLineViewerMetadata(
            '',
            '',
            result.scenario,
            result.movement,
            'direct',
            job.nameFormatted(true)
          ));

      },
      (error: string): void => {

        Logger.error(`[jobsviewer] (stringlineview) - can't find inputs (${job.jobId}): `, errorAsText(error));
      });

  }

  /**
   * direct view the raw artifact, type should be 'json' or 'text to indicate
   * which viewing panel to use for the content.
   *
   * @param job  the job involved (parent of the artifact)
   * @param kind the kind of artifact to view
   * @param type which kind of content (should be 'json' or 'text')
   *
   */
  artifactView(job: SimulationJob, kind: string, type: string): void {

    let url: string = job.artifactURL(kind);

    if (url == '') {

      /* no such artifact exists */

      return ;
    }

    /* figure out what kind of data we are downloading... */

    let mime: string = 'application/octet-stream';
    let txt: boolean = false;
    let name: string = this.basename(url);

    Logger.debug(`[jobsviewer] artifactView - ${kind} <= ${url} [${txt}:${mime}]`, job);

    /* first we need the actual content of the artifact ... */

    this.linesim.artifactContents(url,  txt ? 'text' : 'blob')
      .pipe(first())
      .subscribe({

        next: (content: any): void => {

          let blob: Blob = null;

          if (typeof content === 'string') {

            /* it was presumed to be text */

            blob = new Blob([content], {type: mime});

          } else {

            /* its taken as a binary blob */

            blob = content as Blob;
          }

          /* fetch the contents of the blob... */

          blob.text().then((text: string): void => {

            /*
             * with the content in hand (a Blob), we can send this to the appropriate
             * viewing panel...
             *
             */

            if (type == 'json') {

              /* use the JSON viewer */

              this.ide.launchWindow(new  JSONBrowserMetadata(
                '',
                name,
                text,
                'direct',
                `/tmp/${name}`
              ));

            } else {

              /* use the text viewer */

              this.ide.launchWindow(new TextEditorMetadata(
                '',
                name,
                text,
                'rawtext'
              ));
            }

          })

        },

        error: (err): void => {

          Logger.error(`[jobsviewer] artifactView - can't find content ${url}: `, errorAsText(err));
        }

      });
  }

  /**
   * ensure when we click on the artifact buttons we consume the click, regardless of the artifact
   * actually being downloadable or not.
   *
   * @param event   the click event
   * @param job     the job involved
   * @param kind    the kind of artifact
   * @param missing whethor or not we actually have the artifact
   */
  clickToDownload(event: MouseEvent, job: SimulationJob, kind: string, missing: boolean): void {

    let jvc: JobsViewerComponent = this;

    if (!missing) {

      this.artifactDownload(job, kind, missing);
    }

    event.stopPropagation();
    event.preventDefault();
  }

  /**
   * download one of the simulation artifacts
   *
   * @param job     the job involved (parent of the artifact)
   * @param kind    the kind of artifact to view
   * @param missing if we already know we don't actually have it, then don't download.
   *
   */
  artifactDownload(job: SimulationJob, kind: string, missing: boolean): void {

    let jvc: JobsViewerComponent = this;

    if (missing) {

      /* nothing to do */

      return ;
    }

    let url: string = job.artifactURL(kind);

    if (url == '') {

      /* no such artifact exists */

      return ;
    }

    /* figure out what kind of data we are downloading... */

    let mime: string = 'application/octet-stream';
    let txt: boolean = false;
    let name: string = jvc.basename(url);

    Logger.debug(`[jobsviewer] artifactDownload - ${kind} <= ${url} [${txt}:${mime}]`, job);

    if (/\.json$/i.test(url) || /\.json$/i.test(name)) {

      mime = 'application/json';
      txt  = true;
    }

    if (/\.csv/i.test(url) || /\.csv$/i.test(name)) {

      mime = 'text/csv';
      txt  = true;
    }

    const updateId: string = TaskUpdate.generateId();

    let update: TaskArtifactFetch = new TaskArtifactFetch(updateId, 'Streaming from Google ...', 0, url);
    jvc.taskMonitor.progress(update);

    jvc.linesim.artifactContents(url,  txt ? 'text' : 'blob')
      .pipe(first())
      .subscribe({

        next: (content: any): void => {

          let update: TaskArtifactFetch = new TaskArtifactFetch(updateId, 'Received, preparing ...', 0, url);
          jvc.taskMonitor.progress(update);

          Logger.debug(`[jobsviewer] downloading artifact blob...`);

          let blob: Blob = null;

          if (typeof content === 'string') {

            /* it was presumed to be text */

            blob = new Blob([content], {type: mime});

          } else {

            /* its taken as a binary blob */

            blob = content as Blob;
          }

          const clickURL: string  = URL.createObjectURL(blob);

          /* set up a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href          = clickURL;
          element.download      = name || 'download';

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          let complete: TaskArtifactFetchComplete = new TaskArtifactFetchComplete(updateId, 'Downloading...');

          jvc.taskMonitor.progress(complete);

          element.click();

          /* clean up */

          document.body.removeChild(element);
        },

        error: (err): void => {

          let msg: string = errorAsText(err);
          let fail: TaskArtifactFetchFailed = new TaskArtifactFetchFailed(updateId, msg);

          jvc.taskMonitor.progress(fail);

          Logger.error(`[jobsviewer] artifactDownload - can't find content ${url}: `, msg);
        }

      });

  }

  /** show expanded details for the simulation */
  public viewDetails(job: SimulationJob): void {

    this.modals.open('sim-details-modal', null, null, {job: job});

  }

  /** reset our view to empty */
  public clear(): void {

    this.jobMap.clear();

    this.jobList = [];

  }

  /** update or add these jobs */
  public update(jobs: SimulationJob[], updating: boolean = false): void {

    let jvc: JobsViewerComponent = this;

    /*
     * in updating mode we just want to replace, any jobs on the current page
     * that in this update list.  If it's a new job; then we always pre-pend it;
     * since presumably the user wants to have visibility of actively running
     * simulations, all the time.
     *
     */

    if (updating) {

      Logger.debug('[jobsviewer] update - live update for running jobs: ', jobs);

      for (let job of jobs) {

        const jid: string = job.simKey();

        /*
         * figure out if its still running, or it has completed.  If it has completed, we don't
         * care; the normal updating will catch it. But, if its runnning make sure it is on
         * the run list, and if not, make sure its not in the run list.
         *
         */

       if (job.active()) {

         /* make sure it is not in the runnable list */

         Logger.debug(`[jobsviewer] job ${jid} still active: `, SimulationJob.statusString(job.status));

         if (!jvc.runningJobs.find(x => x.simKey() == jid)) {

           jvc.runningJobs.push(job);
         }

       } else {

          /* make sure it is not in the runnable list */

          Logger.debug(`[jobsviewer] job ${jid} has finished: `, SimulationJob.statusString(job.status));

          jvc.runningJobs = jvc.runningJobs.filter(x => x.simKey() != jid);
        }

        Logger.debug('[jobsviewer] runnables: ', jvc.runningJobs);

        /*
         * now the run list has been updated, re-invoke with the current normal job
         * list...so that the runnables get pre-pended (whatever they now are).
         *
         */

        Logger.debug('[jobsviewer] update - re-updating for runnables...');

        jvc.update([...jvc.jobList]);
      }

      /* all done! */

      Logger.debug('[jobsviewer] update - new jobs list (updated): ', jvc.jobList);

      return ;
    }

    /* normal processing of a new page of results ... */

    Logger.debug('[jobsviewer] update - setting page results...');

    /*
     * normally we get called with the list of jobs (already ordered) to display
     * as the current page of simulations.
     *
     */

    jvc.clear();

    for (let job of jobs) {

      const  jid: string = job.simKey();

      /* upsert the job */

      jvc.jobMap.set(jid, job);

      /*
       * if this job is active, then we shift it over to the runnables list. Because we
       * want to always show the runnables at the top of the page.
       *
       */

      if (job.active()) {

        /* move to the runnables list */

        if (!jvc.runningJobs.find(x => x.simKey() == jid)) {

          Logger.debug(`[jobsviewer] moving job ${jid} to runnables: `, SimulationJob.statusString(job.status));

          jvc.runningJobs.push(job);
        }

      } else {

        /* we push/append, because fetched simulations have a sort order (by date or by name) */

        jvc.jobList.push(job);
      }

    } /* end of merging */

    /* sort the runnables by name, engine and jid, so they are always in a predictable order. */

    jvc.runningJobs.sort(function(a: SimulationJob, b: SimulationJob): number {

      let keyA: string = `${a.details.name}:${a.config.engine}:${a.jobId}`;
      let keyB: string = `${b.details.name}:${b.config.engine}:${b.jobId}`;

      return keyA.localeCompare(keyB);
    });

    /* now prepend the runnable jobs; we always show active stuff at the top. */

    for (let job of jvc.runningJobs) {

      jvc.jobList.unshift(job);
    }

    /*
     * finally, filter out any jobs we know we deleted; we have to do this because the
     * deletion operation is asynchronous, and the simulations table in the database may
     * not be updated yet.
     *
     */

    jvc.jobList = jvc.jobList.filter(x => !jvc.deletedJobs.map(x => x.simKey()).includes(x.simKey()) )

    /*
     * if we were focused on a job before the update, stay focused on it...as long as its on the current page.
     * that is, if they were using the keyboard, and the page refreshes...try to stay focused on that item.
     *
     */

    if (!jvc.jobList.find(x => x.simKey() == jvc.jobCursor)) {

      /* its gone, we're on a different page now. */

      Logger.debug(`[jobsviewer] update - dropping cursor ${jvc.jobCursor}`);

      jvc.jobCursor = '';

    } else {

      Logger.debug(`[jobsviewer] update - refocusing on ${jvc.jobCursor}`);

      setTimeout(() => {
        $(jvc.jobElement(jvc.jobCursor)).trigger('focus');
      }, 100);
    }

    Logger.debug('[jobsviewer] update - new jobs list (page refresh): ', jvc.jobList);
  }

  public resize(): void {

  }

  public focus(): void {

  }

  /** fetch the best icon for this status */
  public imageForStatus(status: SimulationStatusType): string {

    let state: string = SimulationJob.statusString(status).toLowerCase();
    let icon: string  = 'sim-result-none-32.png';

    switch(status) {

      case SimulationStatusType.READY:
        return 'sim-status-ready-64.png';
      case SimulationStatusType.ABORTED:
        return 'sim-result-none-32.png';
      case SimulationStatusType.CANCELED:
        return 'sim-result-none-32.png';
      case SimulationStatusType.DEATH:
        return 'sim-result-crashed-64.png';
      case SimulationStatusType.ERROR:
        return 'sim-result-crashed-64.png';
      case SimulationStatusType.FAILED:
        return 'sim-result-fail-32.png';
      case SimulationStatusType.FAIL:
        return 'sim-result-fail-32.png';
      case SimulationStatusType.SUCCESS:
        return 'sim-result-success-32.png';
      case SimulationStatusType.COMPLETED:
        return 'sim-result-success-32.png';
      case SimulationStatusType.QUEUED:
        return 'sim-result-working-32.png';
      case SimulationStatusType.WORKING:
        return 'sim-result-working-32.png';
      case SimulationStatusType.RUNNING:
        return 'sim-result-working-32.png';
    }

    return icon;
  }

  /** @brief lock user interaction */
  public lock(msg: string = ''): void {

    const jvc: JobsViewerComponent = this;

    jvc.busy = true;

    Logger.debug('[jobsviewer] locking ' + msg);

    // @ts-ignore
    $('.jobs-viewer-job-list').LoadingOverlay('show', {
      text: msg,
      background: 'rgba(255, 255, 255, 0.15)',
      textColor: 'black',
      imageColor: 'black'

    });

    // @ts-ignore
    $('.jobs-viewer-job-list').LoadingOverlay('text', msg);

  }

  /** @brief unlock user interaction */
  public unlock(msg: string = '') {

    const jvc: JobsViewerComponent = this;

    jvc.busy = false;

    Logger.debug('[jobsviewer] unlocking ' + msg);

    // @ts-ignore
    $('.jobs-viewer-job-list').LoadingOverlay('hide');

    // @ts-ignore
    $('.jobs-viewer-job-list').LoadingOverlay('text', msg);
  }

  /** they have changed which simulators we are working with, have to update the jobs list etc. */
  public simulatorsChanged(): void {

    const jvc: JobsViewerComponent = this;

    Logger.debug('[jobsviewer] simulatorsChanged: ', this.selectedSimulators);

    jvc.fetchJobs();

  }

  /** refresh the job list, but with search filtering */
  public stringSearch(): void {

    const jvc: JobsViewerComponent = this;

    Logger.debug('[jobsviewer] stringSearch: ', this.searchFilter);

    /* new set of results, so set the current page back to first page */

    jvc.firstRow   = 0;
    jvc.pageCursor = 1;

    /* refresh */

    jvc.fetchJobs();
  }

  /** trigger simulation of whatever scenarios we have uploaded. */
  public upload(): void {

    let jvc: JobsViewerComponent = this;

    Logger.debug('[jobsviewer] upload - ', jvc.pendingFiles);

    /*
     * double-check the uploaded files to ensure we are only taking in things that
     * look reasonably like scenarios.
     *
     */

    /* first build out file reader observables that we can run in parallel */

    let readLimit: number         = 1024;
    let obs: Observable<Object>[] = [];

    for (let file of jvc.pendingFiles) {

      obs.push(new Observable((sub: Subscriber<{ sample: string, file: File }>): void => {

        const fileReader: FileReader = new FileReader();

        /* when the read is completed */

        fileReader.onload = (ev: ProgressEvent<FileReader>): void => {

          let bytes: Uint8Array = new Uint8Array((<any>ev.target).result);
          let sample: string    = '';

          for (let i = 0; i < bytes.length; i++) {

            sample += String.fromCharCode(bytes[i]);
          }

          /* save our own mapping of the sample data */

          Logger.debug(`[jobsviewer] upload - . sample [${ev.type}] ${file.name}: `, sample.substr(0, 64));

          sub.next({sample, file});
          sub.complete();
        }

        /* when the read fails */

        fileReader.onerror = (error: ProgressEvent<FileReader>): void => {

          sub.error(error);
        };

        /* trigger the file reading (asynchronously) ... */

        fileReader.readAsArrayBuffer(file.slice(0, readLimit));

      }));

    } /* end of observable building */

    /* now run the file readers to get samples of text we can use for content aware checking... */

    forkJoin(obs.map(
      o => o.pipe())).subscribe({
      next: (samples: {sample: string, file: File}[]): void => {

        Logger.debug(`[jobsviewer] upload - samples: `, samples);

        let scenarios: File[] = [];

        for (let item of samples) {

          let override: string|null = ContentAwareTyping.typeByContent(item.sample);

          Logger.debug(`[jobsviewer] content aware type (${item.file.name}): `, override);

          if (override != 'scenario') {

            /* but if it has a .scn file suffix...s'ok */

            if (!/\.scn$/.test(item.file.name)) {

              Logger.error(`[jobsviewer] uploaded file does not appear to be a scenario (${item.file.name}), skipping.`);

              continue;
            }
          }

          /* keep this scenario */

          scenarios.push(item.file);

        }

        Logger.debug(`[jobsviewer] upload - scenarios: `, scenarios);

        /* we configure the options already set as the default (top of the project manager GUI) */

        /* we pick the endpoint already set as the default (top of the project manager GUI) */

        let config: SimConfig = jvc.linesim.getConfiguraiton();

        /* launch! */

        jvc.linesim.launchScenarios(scenarios, config).then(

          (jobs: SimulationJob[]): void => {

            /*
             * if we get this far, the jobs were all queued, and now we just have to make sure
             * they are being monitored.
             *
             */

            Logger.debug(`[jobsviewer] upload - monitoring jobs: `, jobs);

            jvc.watcher.monitor(jobs);

          },

          (error): void => {

            Logger.error(`[jobsviewer] upload - problems launching files: `, errorAsText(error));
          });
      },

      error: (err): void => {

        Logger.error(`[jobsviewer] upload - problems reading files: `, errorAsText(err));

      }

    });

  }

  /** fetch the indicator light to use for the simulation engine's current status */
  engineStatusImage(status: string): string {

    if (status.trim().toLowerCase() == 'up') {

      return '/assets/images/plg.png'

    } else if (status.trim().toLowerCase() == 'down') {

      return '/assets/images/plr.png'

    }

    return '/assets/images/ply.png'
  }

  ngOnDestroy(): void {

    let jvc: JobsViewerComponent = this;

    if (jvc.pingTimerId > 0) {

      window.clearTimeout(jvc.pingTimerId);

      jvc.pingTimerId = -1;
    }

    if (jvc.fetchTimerId > 0) {

      window.clearTimeout(jvc.fetchTimerId);

      jvc.fetchTimerId = -1;
    }
  }

  protected readonly SimulationJob = SimulationJob;

  protected readonly SimulationStatusType = SimulationStatusType;
  protected readonly ago = ago;
}
